import { useState } from "react"
import EagerSVGIcon, { EagerSVGIconMap } from "./EagerSVG"
import { Link } from "react-router-dom"

// BUTTON STYLES
const baseButtonStyles = "lg:text-nowrap rounded-[12px] transition-all duration-200 ease-in-out"
const primaryButtonStyles = `dark:text-darkMode-primaryButtonText dark:hover:text-darkMode-primary dark:bg-darkMode-primary dark:hover:bg-transparent border border-transparent hover:border-solid dark:hover:border-darkMode-primary`
const secondaryButtonStyles = `dark:text-darkMode-primary dark:hover:text-darkMode-primaryText dark:bg-transparent dark:hover:bg-darkMode-primary border dark:border-darkMode-primary hover:border-solid`
const tertiaryButtonStyles = `dark:text-darkMode-primaryText border border-info dark:bg-darkMode-tertiary dark:hover:bg-transparent dark:hover:text-darkMode-tertiary`
const mutedButtonStyles = `dark:text-darkMode-muted dark:bg-transparent border dark:border-darkMode-muted dark:hover:bg-darkMode-muted dark:hover:text-darkMode-primaryText hover:border-solid`
const dangerButtonStyles = `dark:text-darkMode-primaryText dark:hover:text-darkMode-danger dark:bg-darkMode-danger dark:hover:bg-transparent border border-transparent dark:hover:border-darkMode-danger`
const dangerSecondaryButtonStyles = `dark:text-darkMode-danger dark:hover:text-darkMode-primaryText dark:bg-transparent dark:hover:bg-darkMode-danger border dark:border-darkMode-danger dark:hover:border-transparent`
const dangerMutedButtonStyles = `dark:text-darkMode-danger dark:bg-transparent border border-transparent hover:border-solid`
const disabledButtonStyles = `dark:text-darkMode-primaryText dark:bg-darkMode-muted border border-transparent cursor-not-allowed`
const iconOnlyButtonStyles = `w-10 p-2 rounded-full dark:hover:bg-darkMode-layer3color transition-all duration-200 ease-in-out`
const plainButtonStyles = ``

const linkMuted = "dark:text-darkMode-muted dark:hover:text-darkMode-primary underline" // Used for muted links
const linkPrimary = "dark:text-darkMode-primary dark:hover:text-white underline" // Used for primary links
const linkDanger = "dark:text-darkMode-danger dark:hover:text-white underline" // Used for danger links

// BUTTON SIZES
const tinyButtonStyles = "text-[12px] px-4 py-2"
const smallButtonStyles = "text-[12px] px-4 py-2"
const mediumButtonStyles = "text-[14px] px-5 py-2"
const largeButtonStyles = "text-[16px] px-8 py-4"

const smallLinkButtonStyles = "text-[12px] px-0 py-0"
const mediumLinkButtonStyles = "text-[14px] px-0 py-0"
const largeLinkButtonStyles = "text-[16px] px-0 py-0"


export type ButtonStyles = 'primary' | 'secondary' | 'tertiary' | 'muted' | 'danger' | 'dangerSecondary' | 'dangerMuted' | 'disabled' | 'linkMuted' | 'linkPrimary' | 'linkDanger' | 'plain'

type BaseButtonProps = {
    children?: React.ReactNode
    type?: 'button' | 'submit' | 'reset'
    size?: 'tiny' | 'small' | 'medium' | 'large'
    buttonStyle?: ButtonStyles
    disabled?: boolean
    label?: string
    alwaysFitWidth?: boolean
    alwaysFullWidth?: boolean
    fauxButton?: boolean
    buttonCustomClasses?: string
    centered?: boolean
    icon?: EagerSVGIconMap
    iconPlacement?: 'left' | 'right'
    iconOnly?: boolean // If true, the button will only display an icon
}

// Button Props (For `<button>`)
type ButtonProps = BaseButtonProps & {
    onClick: () => void
    href?: never // Ensures "href" is NOT used with buttons
}

// Link Props (For `<a>`)
type LinkProps = BaseButtonProps & {
    href: string
    onClick?: never // Ensures "onClick" is NOT used with links
    target?: string
}

// Faux Button Props (For `<span>`)
type FauxButtonProps = BaseButtonProps & {
    fauxButton: true
    onClick?: never
    href?: never
}

// Submit Button Props (For `<button type="submit">`)
type SubmitButtonProps = BaseButtonProps & {
    type: 'submit'
    onClick?: never
    href?: never
}

// Icon only button props
type IconButtonProps = BaseButtonProps & (LinkProps | ButtonProps | SubmitButtonProps | FauxButtonProps) & {
    icon: EagerSVGIconMap // Icon to be displayed
    iconOnly: true
    iconPlacement?: never
    label: never // Icon buttons typically don't have text labels
    children?: never // Icon buttons don't have children
}

type ButtonComponentProps = ButtonProps | LinkProps | FauxButtonProps | SubmitButtonProps | IconButtonProps

export const Button: React.FC<ButtonComponentProps> = (
    { children,
        type = 'button',
        size = 'medium',
        buttonStyle = 'primary',
        disabled = false,
        label,
        alwaysFitWidth,
        alwaysFullWidth,
        fauxButton,
        buttonCustomClasses,
        centered,
        icon,
        iconPlacement = 'right',
        iconOnly,
        ...props
    }: ButtonComponentProps) => {

    // Determine if the button is a link style
    const isLinkStyle = buttonStyle === "linkMuted" || buttonStyle === "linkPrimary" || buttonStyle === "linkDanger"

    // Choose size styles based on button type and size
    let sizeStyles = ""
    if (isLinkStyle) {
        sizeStyles =
            size === "tiny"
                ? smallLinkButtonStyles // fallback to small for tiny
                : size === "small"
                    ? smallLinkButtonStyles
                    : size === "large"
                        ? largeLinkButtonStyles
                        : mediumLinkButtonStyles
    } else {
        sizeStyles =
            size === "tiny"
                ? tinyButtonStyles
                : size === "small"
                    ? smallButtonStyles
                    : size === "large"
                        ? largeButtonStyles
                        : size === "medium" ? mediumButtonStyles
                            : mediumButtonStyles
    }

    const typeStyles = buttonStyle === 'primary'
        ? primaryButtonStyles
        :
        buttonStyle === 'secondary'
            ? secondaryButtonStyles
            :
            buttonStyle === 'muted'
                ? mutedButtonStyles
                :
                buttonStyle === 'tertiary'
                    ? tertiaryButtonStyles
                    :
                    buttonStyle === 'danger'
                        ? dangerButtonStyles
                        :
                        buttonStyle === "dangerSecondary"
                            ? dangerSecondaryButtonStyles
                            :
                            buttonStyle === 'dangerMuted'
                                ? dangerMutedButtonStyles
                                :
                                buttonStyle === 'linkMuted'
                                    ? linkMuted
                                    :
                                    buttonStyle === 'linkPrimary'
                                        ? linkPrimary
                                        :
                                        buttonStyle === 'linkDanger'
                                            ? linkDanger
                                            :
                                            buttonStyle === 'plain'
                                                ?
                                                plainButtonStyles
                                                :
                                                disabledButtonStyles

    const buttonWidthBehaviour = alwaysFitWidth
        ? "w-fit"
        : alwaysFullWidth
            ? "w-full"
            : "w-full sm:w-fit" // Default behavior: full width on mobile, fit-width on desktop

    const buttonClasses = `${iconOnly ? iconOnlyButtonStyles : baseButtonStyles} ${iconOnly ? '' : sizeStyles} ${disabled ? disabledButtonStyles : typeStyles} ${buttonWidthBehaviour} ${buttonCustomClasses ? buttonCustomClasses : ''} ${centered ? 'mx-auto' : ''}`

    const handleClick = () => {
        if (type !== "submit" && !fauxButton && 'onClick' in props && typeof props.onClick === 'function') {
            props.onClick();
        }
    }

    if (iconOnly) {
        if ("type" in props && props.type === 'submit') {
            return (
                <button type="submit" className={buttonClasses} disabled={disabled}>
                    <EagerSVGIcon name={icon as EagerSVGIconMap} width={24} height={24} />
                </button>
            )
        } else if ("href" in props) {
            return (
                <a href={props.href} target={'target' in props ? props.target as React.HTMLAttributeAnchorTarget : undefined} className={`${buttonClasses} text-center`} rel="noreferrer">
                    <EagerSVGIcon name={icon as EagerSVGIconMap} width={24} height={24} />
                </a>
            )
        } else if (fauxButton) {
            return (
                <span className={`block ${buttonClasses} text-center content-center`}>
                    <EagerSVGIcon name={icon as EagerSVGIconMap} width={24} height={24} />
                </span>
            )
        } else {
            return (
                <button onClick={handleClick} className={buttonClasses} disabled={disabled}>
                    <EagerSVGIcon name={icon as EagerSVGIconMap} width={24} height={24} />
                </button>
            )
        }
    }

    // Faux version of button. Useful if the button is sitting in a big clickable area. See 'Unlock Now' implementation on Feed pages.
    if (fauxButton) {
        return (
            <span className={`block ${buttonClasses} text-center content-center`}>
                {icon && iconPlacement === 'left' && (
                    <EagerSVGIcon name={icon as EagerSVGIconMap} className="inline mr-1 -mt-0.5" width={24} height={24} />
                )}
                {label || children}
                {icon && iconPlacement === 'right' && (
                    <EagerSVGIcon name={icon as EagerSVGIconMap} className="inline ml-1 -mt-0.5" width={24} height={24} />
                )}
            </span>
        )
    }

    // Link version of the button
    if ("href" in props && props.href) {
        return (
            <Link to={props.href} target={'target' in props ? props.target : undefined} className={`${buttonClasses} text-center`} rel="noreferrer">
                {icon && iconPlacement === 'left' && (
                    <EagerSVGIcon name={icon as EagerSVGIconMap} className="inline mr-1 -mt-0.5" width={24} height={24} />
                )}
                {label || children}
                {icon && iconPlacement === 'right' && (
                    <EagerSVGIcon name={icon as EagerSVGIconMap} className="inline ml-1 -mt-0.5" width={24} height={24} />
                )}
            </Link>
        )
    }

    if (type === 'submit') {
        return (
            <button type="submit" className={buttonClasses} disabled={disabled}>
                {icon && iconPlacement === 'left' && (
                    <EagerSVGIcon name={icon as EagerSVGIconMap} className="inline mr-1 -mt-0.5" width={24} height={24} />
                )}
                {label || children}
                {icon && iconPlacement === 'right' && (
                    <EagerSVGIcon name={icon as EagerSVGIconMap} className="inline ml-1 -mt-0.5" width={24} height={24} />
                )}
            </button>
        )
    }

    return (
        <button onClick={handleClick} className={buttonClasses} disabled={disabled}>
            {icon && iconPlacement === 'left' && (
                <EagerSVGIcon name={icon as EagerSVGIconMap} className="inline mr-1 -mt-0.1" width={24} height={24} />
            )}
            {label || children}
            {icon && iconPlacement === 'right' && (
                <EagerSVGIcon name={icon as EagerSVGIconMap} className="inline ml-1 -mt-0.5" width={24} height={24} />
            )}
        </button>
    )
}




// ========================================================
// ================= COPY TO CLIPBOARD ====================
// ========================================================

export const CopyToClipboard = ({ text }: { text: string }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(text)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch (err) {
            console.error("Failed to copy: ", err)
        }
    }

    return (
        <>
            <span>{text}</span>
            <button onClick={handleCopy} className="p-2 rounded-full ml-1.5 dark:hover:bg-darkMode-layer3color">
                {copied ? (
                    <EagerSVGIcon name="SuccessIcon" className="dark:text-darkMode-primary" width={20} height={20} />
                ) : (
                    <EagerSVGIcon name="CopyIcon" className="dark:text-darkMode-primary" width={20} height={20} />
                )}
            </button>
        </>
    )
}
